import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SERVICES } from "@/data/content";
import { useSEO } from "@/lib/seo";
import CTASection from "@/components/CTASection";

function PageHeader({ overline, title, sub }) {
  return (
    <section className="relative overflow-hidden bg-navy-900 noise-overlay">
      <div className="absolute inset-0 grid-pattern opacity-40" aria-hidden="true" />
      <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-16 lg:py-20">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-500">{overline}</p>
        <h1 className="mt-3 max-w-3xl font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-white">{title}</h1>
        {sub && <p className="mt-5 max-w-2xl text-lg leading-relaxed text-slate-300">{sub}</p>}
      </div>
    </section>
  );
}

export default function Services() {
  useSEO({
    title: "Environmental Compliance Services",
    description:
      "Tier II reporting support, EPCRA 311/312, chemical inventory management, SDS management, TRI and more — remote environmental compliance services for U.S. facilities.",
    path: "/services",
  });

  return (
    <>
      <PageHeader
        overline="Our Services"
        title="Environmental compliance support, structured for U.S. industrial facilities"
        sub="A modular set of remote compliance services — add capabilities as your reporting obligations grow."
      />
      <section className="bg-slate-50 py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {SERVICES.map((s) => {
              const Icon = s.icon;
              return (
                <Link
                  key={s.slug}
                  to={`/services/${s.slug}`}
                  className="group flex flex-col rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
                  data-testid={`service-card-${s.slug}`}
                >
                  <div className="flex items-center gap-4">
                    <span className="flex h-12 w-12 items-center justify-center rounded-lg bg-navy-900 text-amber-500">
                      <Icon className="h-6 w-6" aria-hidden="true" />
                    </span>
                    <h2 className="font-heading text-xl font-bold text-navy-900">{s.name}</h2>
                  </div>
                  <p className="mt-4 flex-1 text-sm leading-relaxed text-slate-600">{s.intro}</p>
                  <span className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-navy-900 group-hover:text-amber-600 transition-colors">
                    Explore service <ArrowRight className="h-4 w-4" aria-hidden="true" />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
      <CTASection location="services-footer" />
    </>
  );
}

export { PageHeader };
